import type { AbstractControl } from '@angular/forms';
import type {
  Currency,
  QuotationItemRevision,
  QuotationRevision,
  QuotationItemType,
} from '@freelancer/datastore/collections';

export enum FormKeys {
  START_DATE = 'startDate',
  END_DATE = 'endDate',
  TIMELINE = 'timeline',
  DESCRIPTION = 'description',
  EXPIRY_DATE = 'expiryDate',
  TITLE = 'title',
  DURATION = 'duration',
  DURATION_AMOUNT = 'durationAmount',
  DURATION_UNIT = 'durationUnit',
}

export const DURATION_UNITS = {
  DAYS: $localize`Days`,
  WEEKS: $localize`Weeks`,
  MONTHS: $localize`Months`,
};

export type DURATION_UNITS =
  (typeof DURATION_UNITS)[keyof typeof DURATION_UNITS];

export const DURATION_OPTIONS: readonly DURATION_UNITS[] = [
  DURATION_UNITS.DAYS,
  DURATION_UNITS.WEEKS,
  DURATION_UNITS.MONTHS,
];

// 1 Day
const DURATION_DAY = 1;
// 7 Days
const DURATION_WEEK = 7;
// 30 Days
const DURATION_MONTH = 30;

// Note this should be ordered by highest to lowest, as this is used
// to work out what units were originally used
export const DURATION_TO_DAYS_MAPPING = new Map<string, number>([
  [DURATION_UNITS.MONTHS, DURATION_MONTH],
  [DURATION_UNITS.WEEKS, DURATION_WEEK],
  [DURATION_UNITS.DAYS, DURATION_DAY],
]);

export enum ItemFormKeys {
  NAME = 'name',
  PRICE = 'price',
}

export type ItemDetails = Pick<
  QuotationItemRevision,
  | 'name'
  | 'unitPrice'
  | 'quantity'
  | 'currencyId'
  | 'unitLimit'
  | 'unitType'
  | 'quotationItemType'
  | 'billingFrequency'
>;

// Params for initial item creation
export interface ItemCreationParams {
  quotationId: number;
  quotationVersionId: number;
  details: ItemDetails;
  order: string;
}

// Params for updating an item's mutable properties
export interface ItemUpdateParams {
  quotationItem: QuotationItemRevision;
  details: Partial<ItemDetails>;
}

// Params for immutable item revision creation.
export interface ItemRevisionParams {
  previousItemVersionId: number;
  quotationVersionId: number;
  updateParams: ItemUpdateParams;
}

// Params for updating a Quotation version's details
export type QuotationRevisionUpdateParams = Partial<
  Pick<
    QuotationRevision,
    | 'description'
    | 'richDescription'
    | 'estimatedStartDate'
    | 'estimatedEndDate'
    | 'title'
    | 'expiryDate'
    | 'durationInDays'
    | 'logoId'
    | 'defaultCurrencyId'
  >
>;

// Params for checking form validity
export interface QuotationFormState {
  isEdited: boolean;
  isValid: boolean;
}

export interface FormItemDeleteParams {
  itemControl: AbstractControl;
  itemVersionId?: number;
  formArrayIndex: number;
}

export interface FormItemStatusUpdate {
  itemControl: AbstractControl;
  itemVersionId: number;
}

// Quotation form validation
export const MAX_ITEM_NAME_LENGTH = 50;

export interface FormItemCreateParams {
  type: QuotationItemType;
  isUserInAu: boolean;
  currency: Currency;
}
